import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { Recycle, Clock } from "@phosphor-icons/react";

export default function UsedBank() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/questions/cooldown");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-sm text-nk-mute">Loading…</p>;

  return (
    <div className="space-y-6">
      <Heading
        title="Used Bank · Question Rotation"
        subtitle={`A question is auto-quarantined for ${data.cooldown_days} days once it has been served ${data.max_uses_per_window} times. The picker skips quarantined questions across every mode so players never see the same question twice in two weeks.`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat
          label="Cooldown window"
          value={`${data.cooldown_days} days`}
          icon={<Clock size={14} weight="duotone" />}
        />
        <Stat
          label="Max uses / window"
          value={`${data.max_uses_per_window}`}
          icon={<Recycle size={14} weight="duotone" />}
        />
        <Stat
          label="Currently quarantined"
          value={`${data.in_cooldown_count}`}
          accent={data.in_cooldown_count > 0 ? "warn" : "ok"}
        />
      </div>

      <div className="nk-card overflow-hidden" data-testid="used-bank-table">
        <div className="px-4 py-3 border-b border-nk-line/40 flex items-center justify-between">
          <p className="eyebrow text-nk-mute">Most-used questions in the active window</p>
          <span className="text-[10px] text-nk-dim font-mono">
            Top {data.top_used.length}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wider text-nk-dim">
              <tr className="border-b border-nk-line/40">
                <th className="py-2.5 px-4">Question</th>
                <th className="py-2.5 px-2 text-center">Category</th>
                <th className="py-2.5 px-2 text-center">Uses</th>
                <th className="py-2.5 px-4 text-right">Last used</th>
              </tr>
            </thead>
            <tbody>
              {data.top_used.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-nk-dim text-xs">
                  No questions have been served in the active {data.cooldown_days}-day window.
                </td></tr>
              )}
              {data.top_used.map((r) => {
                const isQuarantined = r.uses >= data.max_uses_per_window;
                return (
                  <tr key={r.qid} className="border-b border-nk-line/20 hover:bg-nk-surface/30">
                    <td className="py-2.5 px-4 text-nk-text max-w-[520px] truncate">{r.body || "(unknown)"}</td>
                    <td className="py-2.5 px-2 text-center text-[11px] text-nk-mute">{r.category || "—"}</td>
                    <td className="py-2.5 px-2 text-center font-mono">
                      <span className={isQuarantined ? "text-nk-bad font-semibold" : "text-nk-warn"}>
                        {r.uses}/{data.max_uses_per_window}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right text-[11px] text-nk-dim font-mono">
                      {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-nk-line/40 text-[10px] text-nk-dim">
          Questions in red have hit the limit and are auto-skipped by every mode (NK Daily, Ladder, Duel, Survival, Saturday, Championship).
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, accent }) {
  const color = accent === "warn" ? "text-nk-warn" : accent === "ok" ? "text-nk-accent" : "text-nk-text";
  return (
    <div className="nk-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-nk-dim flex items-center gap-1.5">
        {icon} {label}
      </p>
      <p className={`font-display text-3xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}
